import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './slides.module.css';
import enhancedStyles from '../CaseBuilderEnhanced.module.css';
import selectorStyles from '../../../../../components/definition/DefinitionSelector.module.css';

function blockMatchesQuery(block, query) {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return true;
    const name = String(block?.name || block?.longName || '').toLowerCase();
    if (name.includes(q)) return true;
    const regions = Array.isArray(block?.regionDefinitions) ? block.regionDefinitions : [];
    return regions.some((r) => {
        const label = String(r?.displayName || r?.abbreviation || r?.name || '').toLowerCase();
        return label.includes(q);
    });
}

function regionNamesForBlock(block) {
    const regions = Array.isArray(block?.regionDefinitions) ? block.regionDefinitions : [];
    return regions
        .map((r) => r?.displayName || r?.abbreviation || r?.name)
        .filter(Boolean);
}

/**
 * Single block picker for the Enhanced assign sheet (choose / change / clear).
 */
export default function SlideBlockPicker({
    blocks = [],
    selectedBlock = null,
    onSelect,
    onClear,
    disabled = false,
    readOnly = false,
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    const filtered = useMemo(
        () => (blocks || []).filter((b) => blockMatchesQuery(b, search)),
        [blocks, search],
    );

    const close = () => {
        setOpen(false);
        setSearch('');
    };

    const pick = (block) => {
        onSelect?.(block);
        close();
    };

    if (readOnly || disabled) {
        return (
            <div className={styles.blockSummary}>
                <span className={styles.blockSummaryName}>
                    {selectedBlock?.name || selectedBlock?.longName || 'No block'}
                </span>
            </div>
        );
    }

    if (!selectedBlock) {
        return (
            <>
                <button
                    type="button"
                    className={styles.blockChooseButton}
                    onClick={() => setOpen(true)}
                >
                    Choose block…
                </button>
                <p className={styles.blockHint}>Regions for this slide come from the block you choose.</p>
                {open && typeof document !== 'undefined' && createPortal(
                    <BlockPickerModal
                        filtered={filtered}
                        selectedBlock={selectedBlock}
                        search={search}
                        setSearch={setSearch}
                        onPick={pick}
                        onClose={close}
                    />,
                    document.body,
                )}
            </>
        );
    }

    return (
        <>
            <div className={styles.blockSummary}>
                <span className={styles.blockSummaryName}>
                    {selectedBlock.name || selectedBlock.longName || 'Block'}
                </span>
                <div className={styles.blockSummaryActions}>
                    <button
                        type="button"
                        className={enhancedStyles.linkButton}
                        onClick={() => setOpen(true)}
                    >
                        Change
                    </button>
                    <button
                        type="button"
                        className={enhancedStyles.linkButton}
                        onClick={() => onClear?.()}
                    >
                        Clear
                    </button>
                </div>
            </div>
            {open && typeof document !== 'undefined' && createPortal(
                <BlockPickerModal
                    filtered={filtered}
                    selectedBlock={selectedBlock}
                    search={search}
                    setSearch={setSearch}
                    onPick={pick}
                    onClose={close}
                />,
                document.body,
            )}
        </>
    );
}

function BlockPickerModal({ filtered, selectedBlock, search, setSearch, onPick, onClose }) {
    return (
        <>
            <div className={selectorStyles.overlay} onClick={onClose} aria-hidden="true" />
            <div className={selectorStyles.modal} role="dialog" aria-modal="true" aria-label="Select block">
                <div className={selectorStyles.modalHeader}>
                    <h3>Select block</h3>
                    <button type="button" className={selectorStyles.closeButton} onClick={onClose} aria-label="Close">×</button>
                </div>
                <p className={styles.pickerHelp}>
                    Select one block. Its regions will be copied onto this slide.
                </p>
                <div className={selectorStyles.modalContent}>
                    <div className={selectorStyles.searchBox}>
                        <input
                            type="search"
                            placeholder="Search blocks or regions…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            autoFocus
                        />
                    </div>
                    <div className={`${selectorStyles.definitionsList} ${selectorStyles.definitionsListCompact}`}>
                        {filtered.map((block) => {
                            const isSelected = selectedBlock && String(selectedBlock._id) === String(block._id);
                            const names = regionNamesForBlock(block);
                            return (
                                <div
                                    key={block._id}
                                    className={`${selectorStyles.definitionCard} ${selectorStyles.definitionCardCompact} ${isSelected ? selectorStyles.selected : ''}`}
                                    onClick={() => onPick(block)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            onPick(block);
                                        }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                >
                                    <div className={selectorStyles.cardHeader}>
                                        <strong>{block.name || block.longName || 'Block'}</strong>
                                        {isSelected && (
                                            <span style={{ marginLeft: 'auto', color: '#28a745', fontWeight: 600 }}>✓</span>
                                        )}
                                    </div>
                                    {names.length > 0 && (
                                        <div className={selectorStyles.cardDetails}>
                                            <span>{names.join(', ')}</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {filtered.length === 0 && (
                            <div className={selectorStyles.emptyState}>
                                {search.trim() ? 'No blocks match your search' : 'No blocks available'}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
